import { useId, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { inspect, MAX_CHARS } from "../lib/inspect";

const SAMPLE = "Hi!";

export function CharInspector() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);

  const rows = useMemo(() => inspect(input), [input]);
  const totalCodePoints = useMemo(() => [...input].length, [input]);
  const truncated = totalCodePoints > MAX_CHARS;

  // Stable keys without the array index (a character can repeat).
  const keyedRows = useMemo(() => {
    const seen: Record<number, number> = {};
    return rows.map((r) => {
      seen[r.codePoint] = (seen[r.codePoint] ?? 0) + 1;
      return { ...r, key: `${r.codePoint}-${seen[r.codePoint]}` };
    });
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="space-y-1.5 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <Label htmlFor={`${uid}-in`}>{m.ci_input_label()}</Label>
        <textarea
          id={`${uid}-in`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={m.ci_placeholder()}
          spellCheck={false}
          className="min-h-[6rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
        />
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.ci_results_label()}
          </h2>
          <span
            data-testid="ci-count"
            className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
          >
            {m.ci_count({ n: String(totalCodePoints) })}
          </span>
        </div>

        {keyedRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{m.ci_empty()}</p>
        ) : (
          <div className="overflow-x-auto">
            <table
              data-testid="ci-table"
              className="w-full border-collapse text-sm"
            >
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">{m.ci_col_char()}</th>
                  <th className="py-2 pr-4 font-medium">
                    {m.ci_col_codepoint()}
                  </th>
                  <th className="py-2 pr-4 font-medium">{m.ci_col_hex()}</th>
                  <th className="py-2 font-medium">{m.ci_col_utf8()}</th>
                </tr>
              </thead>
              <tbody>
                {keyedRows.map((r) => (
                  <tr key={r.key} className="border-border border-t">
                    <td className="py-1.5 pr-4 font-mono text-base">
                      {r.char}
                    </td>
                    <td className="py-1.5 pr-4 tabular-nums">{r.codePoint}</td>
                    <td className="py-1.5 pr-4 font-mono">{r.hex}</td>
                    <td className="py-1.5 font-mono text-muted-foreground">
                      {r.utf8}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {truncated && (
          <p className="text-xs text-muted-foreground">{m.ci_truncated()}</p>
        )}
      </div>
    </div>
  );
}
